import { useMemo, useState } from 'react';
import { ArrowPathIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { useLeaderboard } from '../hooks/useDailyInstallations';

// Installations Leaderboard — the NEO2 rebuild of the Power BI page.
//
// The ranking is a weighted score rather than a count of jobs: 0.6 for a job
// whose externals were already complete, 1.0 otherwise, less 0.2 where a
// speed-support request exists for that address that month, then adjusted for
// assistance given and received. That is the report's own calculation, and it
// is what the business pays against — so the page shows the working, not just
// the rank, and says how much of the work the weighting could be applied to.
//
// Palette follows the report's theme (CY25SU10) as the other rebuilt page does.
const BRAND = '#711C91';
const num = (v) => Number(v || 0).toLocaleString('en-GB');
const dec = (v) => Number(v || 0).toLocaleString('en-GB', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
const mins = (v) => (v === null || v === undefined ? '—' : `${Math.floor(v / 60)}:${String(v % 60).padStart(2, '0')}`);
const thisMonth = () => new Date().toISOString().slice(0, 7);

function Medal({ place }) {
  const tone = ['#D9B300', '#9CA3AF', '#B45309'][place - 1];
  if (!tone) return <span className="w-6 text-right text-xs tabular-nums text-gray-400">{place}</span>;
  return (
    <span
      className="inline-flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-bold text-white"
      style={{ background: tone }}
    >
      {place}
    </span>
  );
}

function Leaderboard() {
  const [month, setMonth] = useState(thisMonth());
  const [region, setRegion] = useState('');
  const [showWorking, setShowWorking] = useState(false);

  const filters = useMemo(() => ({ month, region }), [month, region]);
  const { data, isLoading, isFetching, refetch, isError } = useLeaderboard(filters);

  const rows = data?.engineers || [];
  const cov = data?.coverage || {};
  const top = rows[0];
  const topRegion = (data?.by_region || [])[0];
  const avgMins = rows.length
    ? Math.round(rows.filter((r) => r.avg_minutes).reduce((t, r) => t + r.avg_minutes, 0)
        / Math.max(1, rows.filter((r) => r.avg_minutes).length))
    : null;

  const sel = 'rounded-md border border-gray-300 px-2.5 py-1.5 text-sm focus:border-brand focus:outline-none focus:ring-1 focus:ring-brand';
  const regions = [...new Set((data?.by_region || []).map((r) => r.region))];

  return (
    <div className="p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Installations Leaderboard</h1>
          <p className="text-sm text-gray-500">
            Ranked on completions, weighted for externals and adjusted for assistance.
          </p>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={sel} />
          <select value={region} onChange={(e) => setRegion(e.target.value)} className={sel}>
            <option value="">All regions</option>
            {regions.map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          <button
            type="button" onClick={() => refetch()}
            className="rounded-md border border-gray-300 p-1.5 text-gray-500 hover:border-brand"
            title="Refresh"
          >
            <ArrowPathIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {isError && <p className="mt-4 text-sm text-red-500">Could not load the leaderboard.</p>}
      {isLoading && <p className="mt-4 text-sm text-gray-400">Loading…</p>}

      {data && (
        <>
          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-brand bg-white p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Top engineer</p>
              <p className="mt-1 truncate text-lg font-bold text-gray-900">{top ? top.engineer : '—'}</p>
              <p className="text-xs" style={{ color: BRAND }}>{top ? `${dec(top.adjusted)} adjusted` : ''}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Top region</p>
              <p className="mt-1 truncate text-lg font-bold text-gray-900">{topRegion ? topRegion.region : '—'}</p>
              <p className="text-xs text-gray-400">{topRegion ? `${num(topRegion.completed)} completed` : ''}</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Completed</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{num(cov.completed)}</p>
              <p className="text-xs text-gray-400">{rows.length} engineers</p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-white p-3">
              <p className="text-[11px] font-medium uppercase tracking-wide text-gray-500">Avg install</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-gray-900">{mins(avgMins)}</p>
              <p className="text-xs text-gray-400">hours:minutes</p>
            </div>
          </div>

          {/* The weighting can only be applied where an externals status exists.
              Stating that is the difference between a ranking people trust and
              one somebody disproves. */}
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 p-3">
            <InformationCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs text-amber-800">
              <strong>{num(cov.with_externals)} of {num(cov.completed)} completed jobs</strong>{' '}
              ({((cov.externals_pct || 0) * 100).toFixed(0)}%) have an externals status recorded, so the
              0.6 weighting applies only to those. The rest score a full 1.0 — the same way the Power BI
              report scores them, because the field is empty at source on BRSK jobs.
            </p>
          </div>

          <div className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
              <h2 className="text-sm font-semibold text-gray-700">Ranking</h2>
              <button
                type="button"
                onClick={() => setShowWorking((v) => !v)}
                className="text-xs font-medium text-gray-500 hover:text-brand"
              >
                {showWorking ? 'Hide working' : 'Show working'}
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-3 py-2 w-12" />
                    <th className="px-3 py-2">Engineer</th>
                    <th className="px-3 py-2">Role</th>
                    <th className="px-3 py-2 text-right">Completed</th>
                    {showWorking ? <th className="px-3 py-2 text-right">Weighted</th> : null}
                    {showWorking ? <th className="px-3 py-2 text-right">Assistance</th> : null}
                    <th className="px-3 py-2 text-right">Adjusted</th>
                    <th className="px-3 py-2 text-right">Avg time</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r, i) => (
                    <tr key={r.email} className={i < 3 ? 'bg-brand-light/40' : 'hover:bg-gray-50'}>
                      <td className="px-3 py-1.5"><Medal place={i + 1} /></td>
                      <td className="px-3 py-1.5 font-medium text-gray-800">{r.engineer}</td>
                      <td className="px-3 py-1.5 text-xs text-gray-500">{r.department || '—'}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-700">{num(r.completed)}</td>
                      {showWorking ? (
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{dec(r.score)}</td>
                      ) : null}
                      {showWorking ? (
                        <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">
                          {r.assistance_received || r.assistance_given
                            ? `+${dec(r.assistance_received)} / −${dec(r.assistance_given)}`
                            : '—'}
                        </td>
                      ) : null}
                      <td className="px-3 py-1.5 text-right font-semibold tabular-nums" style={{ color: BRAND }}>
                        {dec(r.adjusted)}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums text-gray-500">{mins(r.avg_minutes)}</td>
                    </tr>
                  ))}
                  {!rows.length ? (
                    <tr><td colSpan={showWorking ? 8 : 6} className="px-3 py-10 text-center text-gray-400">
                      Nothing completed in this period.
                    </td></tr>
                  ) : null}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-100 px-4 py-2 text-[11px] text-gray-400">
              Adjusted = weighted completions − assistance given out + assistance received in.
              A job counts 0.6 where its externals were already complete, 1.0 otherwise,
              less 0.2 where a speed-support request was raised for that address that month.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

export default Leaderboard;
